import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Modal,
  Box,
  Button,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Api } from "queries/types";
import { useGridProposalCreate } from "queries/grid_proposal";
import { deleteShopGrid } from "queries/shop_grid";
import useAlert from "components/useAlert";
import { useAuthorization, Feature } from "user";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  maxWidth: "90%",
  boxSizing: "border-box",
  bgcolor: "background.paper",
  border: "1px solid #000",
  boxShadow: 24,
  p: 4,
};

interface InventoryProposalModalProps {
  shop: Api.Shop;
  onClose: (gridId?: number) => void;
}

const InventoryProposalModal = ({ shop, onClose }: InventoryProposalModalProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const createMutation = useGridProposalCreate();
  const [selectedGridId, setSelectedGridId] = useState<number | null>(null);
  const [name, setName] = useState<string>("");
  const hasAccess = useAuthorization();
  const { setAlert } = useAlert();

  const grids = shop.shopGrids.filter((grid) => !grid.isActive);
  const displaySelect = !name.length && grids.length > 0;
  const displayTextInput = !selectedGridId;

  const submit = async (e: any) => {
    e.preventDefault();
    if (!selectedGridId && !name.length) return;
    if (selectedGridId) return navigate(`/shops/${shop.erpId}/inventory_proposal/${selectedGridId}`);

    const { data: grid } = await createMutation.mutateAsync({ erpId: shop.erpId, name });
    navigate(`/shops/${shop.erpId}/inventory_proposal/${grid.id}`);
  };

  const deleteProposal = async (e: any) => {
    e.preventDefault();
    if (!selectedGridId) return;
    deleteShopGrid(selectedGridId)
      .then(() => {
        onClose(selectedGridId);
      }).catch((status) => {
        setAlert(status?.response?.data?.detail, 'error');
      });
  };

  return (
    <Modal open onClose={() => onClose()}>
      <Box sx={style}>
        <form onSubmit={submit}>
          {displaySelect && (
            <FormControl fullWidth>
              <InputLabel>{t("shop.index.select_proposal")}</InputLabel>
              <Select
                label={t("shop.index.select_proposal")}
                value={selectedGridId || ""}
                onChange={(e) => setSelectedGridId(e.target.value as number)}
              >
                <MenuItem key="none" value="">
                  {t("shop.index.select_proposal")}
                </MenuItem>
                {grids.map((grid) => (
                  <MenuItem key={grid.id} value={grid.id}>
                    {grid.formattedName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {displaySelect && displayTextInput && <Box sx={{ width: "100%", textAlign: "center", mt: 2 }}>{t("or")}</Box>}
          {displayTextInput && (
            <FormControl fullWidth>
              <TextField
                label={t("shop.index.create_proposal")}
                variant="standard"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <FormHelperText>{t("shop.index.enter_proposal_name")}</FormHelperText>
            </FormControl>
          )}
          <LoadingButton
            loading={createMutation.isLoading}
            variant="contained"
            fullWidth
            sx={{ mt: 2 }}
            type="submit"
            disabled={!selectedGridId && !name.length}
          >
            {t("validate")}
          </LoadingButton>
          <Button fullWidth sx={{ mt: 2 }} onClick={() => onClose()}>
            {t("cancel")}
          </Button>
          {hasAccess(Feature.DeleteProposal) &&
            <LoadingButton
              loading={createMutation.isLoading}
              variant="contained"
              fullWidth
              sx={{ mt: 2 }}
              type="button"
              onClick={deleteProposal}
              disabled={!selectedGridId}
            >
              {t("delete")}
            </LoadingButton>
          }
        </form>
      </Box>
    </Modal>
  );
};

export default InventoryProposalModal;
